<template>
  <div class="question-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1>{{ isEdit ? '编辑题目' : '创建题目' }}</h1>
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/admin/question-banks">题库管理</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link :to="`/admin/question-banks/${bankId}`">
              {{ bankInfo?.subdomain || '题库详情' }}
            </router-link>
          </li>
          <li class="breadcrumb-item active">{{ isEdit ? '编辑题目' : '创建题目' }}</li>
        </ol>
      </nav>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">加载中...</span>
      </div>
      <p class="mt-3 text-muted">正在加载题目信息...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <i class="bi bi-exclamation-triangle me-2"></i>
        {{ error }}
      </div>
      <button class="btn btn-outline-primary" @click="loadQuestion">
        <i class="bi bi-arrow-clockwise me-1"></i>
        重试
      </button>
    </div>

    <!-- 表单内容 -->
    <div v-else class="card">
      <div class="card-header">
        <h5 class="mb-0">{{ isEdit ? '编辑题目信息' : '创建新题目' }}</h5>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <!-- 基本信息 -->
          <div class="row">
            <div class="col-md-8">
              <div class="mb-3">
                <label for="question" class="form-label">
                  题目内容 <span class="text-danger">*</span>
                </label>
                <textarea
                  class="form-control"
                  id="question"
                  v-model="form.question"
                  rows="3"
                  placeholder="请输入题目内容"
                  :class="{ 'is-invalid': errors.question }"
                ></textarea>
                <div v-if="errors.question" class="invalid-feedback">
                  {{ errors.question }}
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="mb-3">
                <label for="type" class="form-label">
                  题型 <span class="text-danger">*</span>
                </label>
                <select
                  class="form-select"
                  id="type"
                  v-model="form.type"
                  :class="{ 'is-invalid': errors.type }"
                  @change="handleTypeChange"
                >
                  <option value="">请选择题型</option>
                  <option value="singleChoice">单选题</option>
                  <option value="multipleChoice">多选题</option>
                  <option value="trueFalse">判断题</option>
                  <option value="essay">简答题</option>
                  <option value="calculation">计算题</option>
                </select>
                <div v-if="errors.type" class="invalid-feedback">
                  {{ errors.type }}
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="mb-3">
                <label for="chapter" class="form-label">
                  章节 <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  id="chapter"
                  v-model="form.chapter"
                  placeholder="请输入章节名称"
                  :class="{ 'is-invalid': errors.chapter }"
                />
                <div v-if="errors.chapter" class="invalid-feedback">
                  {{ errors.chapter }}
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label for="difficulty" class="form-label">
                  难度 <span class="text-danger">*</span>
                </label>
                <select
                  class="form-select"
                  id="difficulty"
                  v-model.number="form.difficulty"
                  :class="{ 'is-invalid': errors.difficulty }"
                >
                  <option value="">请选择难度</option>
                  <option :value="1">简单</option>
                  <option :value="2">中等</option>
                  <option :value="3">困难</option>
                </select>
                <div v-if="errors.difficulty" class="invalid-feedback">
                  {{ errors.difficulty }}
                </div>
              </div>
            </div>
          </div>

          <!-- 选项设置（单选题和多选题） -->
          <div v-if="showOptions" id="options-container" class="mb-4">
            <h6 class="mb-3">选项设置</h6>
            <div
              v-for="(option, key) in form.options"
              :key="key"
              class="row mb-2"
            >
              <div class="col-auto d-flex align-items-center">
                <span class="badge bg-secondary">{{ key }}</span>
              </div>
              <div class="col">
                <input
                  type="text"
                  class="form-control"
                  :id="`option-${key}`"
                  v-model="form.options[key]"
                  :placeholder="`选项 ${key}`"
                />
              </div>
              <div class="col-auto">
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  @click="removeOption(key)"
                  v-if="Object.keys(form.options).length > 2"
                  :title="`删除选项 ${key}`"
                  style="min-width: 36px;"
                >
                  <i class="bi bi-trash" aria-hidden="true"></i>
                  <span class="visually-hidden">删除选项 {{ key }}</span>
                </button>
              </div>
            </div>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="addOption"
              v-if="Object.keys(form.options).length < 6"
            >
              <i class="bi bi-plus-lg me-1"></i>
              添加选项
            </button>
          </div>

          <!-- 答案设置 -->
          <div class="mb-4">
            <h6 class="mb-3">正确答案</h6>
            
            <!-- 单选题答案 -->
            <div v-if="form.type === 'singleChoice'">
              <select
                class="form-select"
                id="answer-single"
                v-model="form.answer"
                :class="{ 'is-invalid': errors.answer }"
              >
                <option value="">请选择答案</option>
                <option v-for="key in Object.keys(form.options)" :key="key" :value="key">
                  {{ key }}. {{ form.options[key] }}
                </option>
              </select>
            </div>

            <!-- 多选题答案 -->
            <div v-else-if="form.type === 'multipleChoice'">
              <div class="form-check" v-for="key in Object.keys(form.options)" :key="key">
                <input
                  type="checkbox"
                  class="form-check-input"
                  :id="`answer-multiple-${key}`"
                  :value="key"
                  v-model="multipleAnswers"
                />
                <label :for="`answer-multiple-${key}`" class="form-check-label">
                  {{ key }}. {{ form.options[key] }}
                </label>
              </div>
            </div>

            <!-- 判断题答案 -->
            <div v-else-if="form.type === 'trueFalse'">
              <div class="form-check">
                <input
                  type="radio"
                  class="form-check-input"
                  id="answer-true"
                  value="正确"
                  v-model="form.answer"
                />
                <label for="answer-true" class="form-check-label">正确</label>
              </div>
              <div class="form-check">
                <input
                  type="radio"
                  class="form-check-input"
                  id="answer-false"
                  value="错误"
                  v-model="form.answer"
                />
                <label for="answer-false" class="form-check-label">错误</label>
              </div>
            </div>

            <!-- 简答题和计算题答案 -->
            <div v-else>
              <textarea
                class="form-control"
                id="answer-short"
                v-model="form.answer"
                rows="3"
                placeholder="请输入参考答案"
                :class="{ 'is-invalid': errors.answer }"
              ></textarea>
            </div>

            <div v-if="errors.answer" class="invalid-feedback d-block">
              {{ errors.answer }}
            </div>
          </div>

          <!-- 解析 -->
          <div class="mb-4">
            <label for="explanation" class="form-label">题目解析</label>
            <textarea
              class="form-control"
              id="explanation"
              v-model="form.explanation"
              rows="4"
              placeholder="请输入题目解析（可选）"
            ></textarea>
          </div>

          <!-- 状态设置 -->
          <div class="mb-4">
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="isActive"
                v-model="form.isActive"
              />
              <label for="isActive" class="form-check-label">
                启用此题目
              </label>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div class="d-flex justify-content-end gap-2">
            <button
              type="button"
              class="btn btn-secondary"
              data-test="cancel-btn"
              @click="handleCancel"
            >
              取消
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="submitting"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
              {{ isEdit ? '更新' : '创建' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { questionBanksApi } from '@/api/questionBanks'
import type { Question, QuestionBank } from '@/types'

const route = useRoute()
const router = useRouter()
const toast = useToast()

// 状态
const loading = ref(false)
const submitting = ref(false)
const error = ref('')
const bankInfo = ref<QuestionBank | null>(null)
const question = ref<Question | null>(null)

const form = ref({
  type: 'singleChoice' as Question['type'],
  chapter: '',
  question: '',
  options: { A: '', B: '', C: '', D: '' } as Record<string, string>,
  answer: '' as any,
  explanation: '',
  difficulty: 2 as Question['difficulty'],
  isActive: true
})

const errors = ref({
  type: '',
  chapter: '',
  question: '',
  answer: '',
  difficulty: ''
})

const multipleAnswers = ref<string[]>([])

// 计算属性
const bankId = computed(() => Number(route.params.bankId))
const questionId = computed(() => Number(route.params.questionId))
const isEdit = computed(() => !!questionId.value)

const showOptions = computed(() => 
  form.value.type === 'singleChoice' || form.value.type === 'multipleChoice'
)

// 方法
const loadBankInfo = async () => {
  try {
    const response = await questionBanksApi.getQuestionBank(bankId.value)
    bankInfo.value = response
  } catch (err) {
    toast.error('加载题库信息失败')
  }
}

const loadQuestion = async () => {
  if (!isEdit.value) return

  loading.value = true
  error.value = ''

  try {
    const response = await questionBanksApi.getQuestion(questionId.value)
    question.value = response
    
    // 填充表单数据
    form.value = {
      type: response.type,
      chapter: response.chapter,
      question: response.question,
      options: response.options || { A: '', B: '', C: '', D: '' },
      answer: response.answer,
      explanation: response.explanation || '',
      difficulty: response.difficulty,
      isActive: response.isActive
    }

    // 处理多选题答案
    if (response.type === 'multipleChoice') {
      if (Array.isArray(response.answer)) {
        multipleAnswers.value = response.answer
      } else if (typeof response.answer === 'string' && response.answer) {
        // 处理字符串格式的答案，如 "A,B,C"
        multipleAnswers.value = response.answer.split(',').map(s => s.trim()).filter(s => s)
      } else {
        multipleAnswers.value = []
      }
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : '加载题目失败'
    toast.error('加载题目失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

const validateForm = () => {
  errors.value = {
    type: '',
    chapter: '',
    question: '',
    answer: '',
    difficulty: ''
  }

  let isValid = true

  if (!form.value.question.trim()) {
    errors.value.question = '题目内容不能为空'
    isValid = false
  }

  if (!form.value.chapter.trim()) {
    errors.value.chapter = '章节不能为空'
    isValid = false
  }

  if (!form.value.difficulty) {
    errors.value.difficulty = '请选择难度'
    isValid = false
  }

  if (!form.value.type) {
    errors.value.type = '请选择题型'
    isValid = false
  }

  // 验证答案
  if (form.value.type === 'singleChoice' || form.value.type === 'trueFalse') {
    if (!form.value.answer) {
      errors.value.answer = '请选择正确答案'
      isValid = false
    }
  } else if (form.value.type === 'multipleChoice') {
    if (multipleAnswers.value.length < 1) {
      errors.value.answer = '多选题至少需要选择一个答案'
      isValid = false
    }
  } else if (form.value.type === 'essay' || form.value.type === 'calculation') {
    if (!form.value.answer.trim()) {
      errors.value.answer = '请输入参考答案'
      isValid = false
    }
  }

  return isValid
}

const handleTypeChange = () => {
  // 重置答案和选项
  form.value.answer = ''
  multipleAnswers.value = []
  
  if (form.value.type === 'singleChoice' || form.value.type === 'multipleChoice') {
    form.value.options = { A: '', B: '', C: '', D: '' }
  } else {
    form.value.options = {}
  }
}

const addOption = () => {
  const keys = Object.keys(form.value.options)
  const nextKey = String.fromCharCode(65 + keys.length) // A, B, C, D, E, F...
  form.value.options[nextKey] = ''
}

const removeOption = (key: string) => {
  delete form.value.options[key]
  form.value.options = { ...form.value.options }
}

const handleSubmit = async () => {
  if (!validateForm()) return

  submitting.value = true

  try {
    // 处理多选题答案
    const finalAnswer = form.value.type === 'multipleChoice' 
      ? multipleAnswers.value.join(',') // 将数组转换为逗号分隔的字符串
      : form.value.answer

    const data = {
      ...form.value,
      answer: finalAnswer
    }

    if (isEdit.value) {
      await questionBanksApi.updateQuestion(questionId.value, data)
      toast.success('题目更新成功')
      router.push(`/admin/question-banks/${bankId.value}`)
    } else {
      await questionBanksApi.createQuestion(bankId.value, data)
      toast.success('题目创建成功')
      router.push(`/admin/question-banks/${bankId.value}`)
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : '操作失败'
    error.value = message
    toast.error(message)
  } finally {
    submitting.value = false
  }
}

const handleCancel = () => {
  if (confirm('确定要取消吗？未保存的更改将会丢失。')) {
    router.back()
  }
}

// 监听多选题答案变化
watch(multipleAnswers, (newAnswers) => {
  if (form.value.type === 'multipleChoice') {
    form.value.answer = newAnswers.join(',') // 将数组转换为逗号分隔的字符串
  }
})

// 生命周期
onMounted(() => {
  loadBankInfo()
  loadQuestion()
})
</script>

<style scoped>
.question-management {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.breadcrumb {
  margin-bottom: 0;
}

/* 选项设置样式优化 */
#options-container .row {
  align-items: center;
}

#options-container .badge {
  font-size: 0.875rem;
  min-width: 24px;
  text-align: center;
}

/* 删除按钮样式优化 */
.btn-outline-danger.btn-sm {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.btn-outline-danger.btn-sm:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
}

/* 答案选择区域样式 */
.form-check {
  padding: 0.5rem 0;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

/* 表单验证样式 */
.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}

@media (max-width: 768px) {
  .question-management {
    padding: 1rem;
  }
  
  #options-container .col-auto {
    margin-bottom: 0.5rem;
  }
}
</style>